"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useMobileView } from "@/components/LargeContents/MessengerComponent/hooks/useMobileView";

// Sur ordinateur, jusqu'à cinq boutons tiennent dans un en-tête de 400 px :
// ils sont resserrés pour laisser sa place au titre. Sur téléphone ils gardent
// une taille confortable au doigt.
export const HEADER_BUTTON =
    "h-9 w-9 md:h-7 md:w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-400 outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors duration-100 hover:bg-gray-100 hover:text-gray-700 dark:text-grayDark-300 dark:hover:bg-white/10 dark:hover:text-white";

export const HEADER_ICON = "h-5 w-5 md:h-[18px] md:w-[18px]";

export function HeaderButton({ icon, label, onClick, className = "" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={`${HEADER_BUTTON} ${className}`}
        >
            <SolarIcon name={icon} className={HEADER_ICON} />
        </button>
    );
}

const SlotContext = createContext(false);

/**
 * L'emplacement d'une fenêtre flottante : son cadre, plein écran sur
 * téléphone, carte en bas à droite sur ordinateur. Il ne sait rien de ce qu'il
 * contient.
 *
 * Le cadre survit à un changement de contenu : quand une question vient d'être
 * publiée, sa fenêtre devient le fil de la discussion sans que le cadre
 * disparaisse. `contentKey` dit quel contenu est affiché ; quand il change,
 * l'ancien s'efface et le nouveau arrive en fondu, à la même place.
 *
 * `minimized` : la fenêtre ne garde que son en-tête (ordinateur seulement,
 * l'appelant ne le passe pas sur téléphone). `wide` : une fenêtre agrandie,
 * plus large et plus haute ; le passage d'une taille à l'autre est animé.
 */
export function FloatingWindowSlot({
    contentKey,
    minimized = false,
    wide = false,
    children,
}) {
    const isMobileView = useMobileView();

    // Plein écran sur téléphone : la page derrière ne défile pas.
    useEffect(() => {
        if (!isMobileView) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isMobileView]);

    // La taille ne s'anime qu'entre la fenêtre normale et la fenêtre agrandie.
    // Replier sur l'en-tête reste instantané : une fenêtre repliée n'a pas de
    // hauteur fixée, rien vers quoi animer. Au rendu où `minimized`
    // change, la transition est donc retirée, puis remise juste après.
    const [settledMinimized, setSettledMinimized] = useState(minimized);
    useEffect(() => {
        setSettledMinimized(minimized);
    }, [minimized]);
    const animateWidth = settledMinimized === minimized;

    const size = minimized
        ? "md:w-[300px]"
        : wide
          ? "md:h-[min(760px,calc(100dvh-96px))] md:w-[560px]"
          : "md:h-[min(620px,calc(100dvh-96px))] md:w-[400px]";

    /* Pas de classe overflow-hidden ni overflow-y-auto sur cette fenêtre :
       globals.css les passe toutes en « visible » dès qu'une boîte de dialogue
       de l'éditeur s'ouvre (le bloc de code), et le contenu se répandait hors
       de la fenêtre. Les mêmes règles sont écrites en propriétés arbitraires. */
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            // Les voisines glissent quand une fenêtre s'ouvre, se ferme ou se
            // réduit, au lieu de sauter.
            layout="position"
            className={`pointer-events-auto fixed inset-0 z-[70] flex flex-col [overflow:hidden] bg-gray-50 dark:bg-bgDark-900 md:static md:flex-shrink-0 md:rounded-2xl md:border md:border-gray-200 md:shadow-2xl md:shadow-black/15 dark:md:border-borderDark dark:md:shadow-black/40 ${size} ${
                animateWidth
                    ? "md:transition-[width,height] md:duration-300 md:ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
                    : ""
            }`}
        >
            <SlotContext.Provider value>
                {/* Le premier contenu arrive avec le cadre ; seuls les suivants
                    passent par le fondu. */}
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={contentKey}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        className="flex min-h-0 flex-1 flex-col"
                    >
                        {children}
                    </motion.div>
                </AnimatePresence>
            </SlotContext.Provider>
        </motion.div>
    );
}

/**
 * Le contenu d'une fenêtre flottante : il nomme la fenêtre pour les lecteurs
 * d'écran. À rendre dans un FloatingWindowSlot, qui porte le cadre.
 */
export default function FloatingWindowFrame({ label, children }) {
    const inSlot = useContext(SlotContext);
    return (
        <div
            role="dialog"
            aria-label={label}
            className={
                inSlot
                    ? "flex min-h-0 flex-1 flex-col"
                    : "pointer-events-auto flex flex-col"
            }
        >
            {children}
        </div>
    );
}
